/* Interactive product tour controller — Feb 2026.
 *
 * Wraps react-joyride with IB-Club brand styling. Reads tour state from
 * backend (/api/users/me/tour-state) and auto-fires the 'dashboard' tour on
 * a member's first ever dashboard visit. Manual tours are triggered via
 * TourContext's `startTour(key)`.
 *
 * Phase 53 fix (Feb 2026) — earlier the dark overlay blocked the entire
 * site even after the tour stalled, and dashboard tour steps targeted
 * testids (streak-card, dashboard-my-progress, etc.) that don't exist on
 * the current dashboard. We now:
 *   1. Pre-filter steps at start time to keep only ones whose target is
 *      actually in the DOM (so Next always advances cleanly).
 *   2. Soften the overlay (semi-transparent) and let clicking outside the
 *      tooltip cancel the tour — never trap the user.
 *   3. Show a permanent floating "Exit tour" badge so escape is always
 *      one click away regardless of Joyride's internal button styling.
 */
import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Joyride, STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { X } from 'lucide-react';
import { useTour } from '../context/TourContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Brand-matched styling. Phase 53 — softened overlay so the site stays
// visually accessible even while the tour is running.
const JOYRIDE_STYLES = {
  options: {
    primaryColor: '#d4af37',
    backgroundColor: '#0f172a',
    textColor: '#f1f5f9',
    arrowColor: '#0f172a',
    overlayColor: 'rgba(0, 0, 0, 0.45)',
    zIndex: 10000,
  },
  tooltip: {
    backgroundColor: '#0f172a',
    border: '1px solid rgba(212, 175, 55, 0.4)',
    borderRadius: 12,
    padding: '18px 22px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)',
  },
  tooltipTitle: { color: '#d4af37', fontSize: 15, fontWeight: 600, marginBottom: 4 },
  tooltipContent: { color: '#cbd5e1', fontSize: 13, lineHeight: '1.6' },
  buttonNext: {
    backgroundColor: '#d4af37',
    color: '#0f172a',
    fontWeight: 600,
    borderRadius: 8,
    padding: '6px 18px',
  },
  buttonBack: { color: '#94a3b8', marginRight: 12 },
  buttonSkip: {
    color: '#f1f5f9',
    fontSize: 12,
    fontWeight: 500,
    backgroundColor: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: 6,
    padding: '6px 12px',
    marginRight: 'auto',  // pushes Skip to the left, Next to the right
    textDecoration: 'none',
  },
  buttonClose: { color: '#64748b' },
  beacon: { display: 'none' },
};

// Tour catalog — key → { route, steps[] }. Each step's `target` is a CSS
// selector. We pre-filter at run time to keep only steps whose target is
// actually mounted on the page, so the tour never stalls on a missing
// element. The first step in each tour MUST target 'body' as a guaranteed
// fallback.
export const TOURS = {
  dashboard: {
    route: '/dashboard',
    title: 'Dashboard tour',
    steps: [
      {
        target: 'body',
        title: 'Welcome to IB Club',
        content: "This 30-second tour shows you the essentials. You can skip anytime, or replay it later from Help.",
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="dashboard-funding-promo"]',
        title: 'Need business funding?',
        content: 'Match with 20+ banks, NBFCs, and P2P lenders. See indicative offers in seconds — no platform fee.',
      },
      {
        target: '[data-testid="admin-section"]',
        title: 'Admin Tools',
        content: 'Manage members, payments, chapters, content, and platform-wide settings.',
      },
      {
        target: '[data-testid="admin-hub-link"]',
        title: 'Explore Admin Hub',
        content: 'Open the admin hub for all your platform controls in one place.',
      },
      {
        target: 'body',
        title: "You're all set",
        content: 'Replay this tour or browse the full Help Center any time from the header menu.',
        placement: 'center',
      },
    ],
  },
  members: {
    route: '/members',
    title: 'Member directory tour',
    steps: [
      {
        target: 'body',
        title: 'Member Directory',
        content: 'Browse founders, investors, and operators in the IB Club network. Filter by chapter, sector, or tier.',
        placement: 'center',
        disableBeacon: true,
      },
    ],
  },
  mentors: {
    route: '/mentors',
    title: 'Mentors tour',
    steps: [
      {
        target: 'body',
        title: 'Find a Mentor',
        content: 'Browse vetted mentors — founders, operators, and investors. Many offer a free 30-min intro.',
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="mentors-filter-bar"]',
        title: 'Filter & search',
        content: 'Search by topic, filter by Free intro or Paid sessions.',
      },
      {
        target: '[data-testid="become-mentor-cta"]',
        title: 'Become a Mentor',
        content: "Sharing expertise? Apply here. Set your price (we keep 20%), accept sessions on your terms.",
      },
    ],
  },
  deal_room: {
    route: '/deal-room',
    title: 'Deal Room tour',
    steps: [
      {
        target: 'body',
        title: 'Investor Deal Room',
        content: 'Private fundraising deals. Metadata is open to all members; deck + financials are NDA-gated.',
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="create-deal-cta"]',
        title: 'List your deal',
        content: 'Founders can list a round here. You get a viewer log of every investor who signs your NDA.',
      },
    ],
  },
};

export const TOUR_ORDER = ['dashboard', 'members', 'mentors', 'deal_room'];

export default function TourController() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { runKey, startTour, cancelTour, finishTour } = useTour();
  const [stepIndex, setStepIndex] = useState(0);
  const [filteredSteps, setFilteredSteps] = useState([]);
  const autoFiredRef = useRef(false);

  // Whenever a tour starts (runKey changes), wait one paint cycle for any
  // newly-mounted page elements to settle, then keep only steps whose
  // target is actually in the DOM. The first 'body'-targeted step is
  // always preserved as a safety net.
  useEffect(() => {
    setStepIndex(0);
    if (!runKey || !TOURS[runKey]) {
      setFilteredSteps([]);
      return undefined;
    }
    const tour = TOURS[runKey];
    if (pathname !== tour.route) {
      // We're not on the tour's page yet — defer filtering until we are.
      setFilteredSteps([]);
      return undefined;
    }
    // Single rAF + 250ms timeout lets dashboard cards finish lazy-loading.
    const tid = setTimeout(() => {
      const kept = tour.steps.filter((s) => {
        if (!s.target || s.target === 'body') return true;
        try {
          return !!document.querySelector(s.target);
        } catch {
          return false;
        }
      });
      // Always keep at least the first welcome step so the user gets
      // SOMETHING even on a stripped-down page.
      setFilteredSteps(kept.length > 0 ? kept : [tour.steps[0]]);
    }, 250);
    return () => clearTimeout(tid);
  }, [runKey, pathname]);

  // Body class so other components can style around an active tour
  useEffect(() => {
    if (runKey && filteredSteps.length > 0) {
      document.body.classList.add('tour-active');
    } else {
      document.body.classList.remove('tour-active');
    }
    return () => document.body.classList.remove('tour-active');
  }, [runKey, filteredSteps.length]);

  // Auto-fire dashboard tour on first ever visit.
  // Phase 51 fix — IB Club uses cookie auth; reading localStorage.token here
  // was always falsy, so the auto-fire would silently bail for everyone
  // logged in via the standard email/password flow. We now probe the
  // cookie-auth'd /api/auth/me endpoint instead.
  useEffect(() => {
    if (autoFiredRef.current) return;
    if (runKey) return;
    if (pathname !== '/dashboard') return;

    autoFiredRef.current = true;
    (async () => {
      try {
        // 1) Confirm auth via cookie. If 401, bail silently (TourController
        //    never auto-fires for logged-out visitors).
        const meR = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
        if (!meR.ok) return;

        // 2) Read tour state — has the user already completed/dismissed the
        //    dashboard tour?
        const r = await fetch(`${API}/api/users/me/tour-state`, {
          credentials: 'include',
        });
        if (!r.ok) return;
        const data = await r.json();
        const seen = new Set([...(data.completed || []), ...(data.dismissed || [])]);
        if (seen.has('dashboard')) return;

        // 3) Wait up to 30s for other first-visit modals (referral popup,
        //    profile completion, cookie banner, etc.) to be dismissed before
        //    firing the tour. We poll every 1s — once nothing is in the way,
        //    we wait 600ms (for animations) and start the tour.
        const startedAt = Date.now();
        const tryFire = () => {
          if (runKey) return; // someone else already fired a tour
          const blocked = !!(
            document.querySelector('[data-testid="referral-upsell-popup"]') ||
            document.querySelector('[data-testid="profile-completion-modal"]') ||
            document.querySelector('[data-testid="cookie-consent-banner"]')
          );
          const tooLate = Date.now() - startedAt > 30_000;
          if (blocked && !tooLate) {
            setTimeout(tryFire, 1000);
            return;
          }
          // Clear field — fire the tour
          setTimeout(() => startTour('dashboard'), 600);
        };
        // Initial delay so the dashboard finishes painting + any popup has a
        // chance to open first.
        setTimeout(tryFire, 2_000);
      } catch { /* non-fatal */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // If a tour is running but we're not on its route, navigate to it
  useEffect(() => {
    if (!runKey) return;
    const tour = TOURS[runKey];
    if (tour && tour.route && pathname !== tour.route) {
      navigate(tour.route);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runKey]);

  if (!runKey || !TOURS[runKey]) return null;
  const tour = TOURS[runKey];
  if (pathname !== tour.route) return null;
  if (filteredSteps.length === 0) return null;

  const markBackend = async (key, completed) => {
    try {
      await fetch(`${API}/api/users/me/tour-state`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tour_key: key, completed }),
      });
    } catch { /* non-fatal */ }
  };

  const handleCallback = (data) => {
    const { status, action, type, index } = data;

    if (status === STATUS.FINISHED) {
      markBackend(runKey, true);
      finishTour();
      return;
    }
    if (status === STATUS.SKIPPED || action === ACTIONS.CLOSE) {
      markBackend(runKey, false);
      cancelTour();
      return;
    }
    if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) {
        if (index + 1 >= filteredSteps.length) {
          markBackend(runKey, true);
          finishTour();
        } else {
          setStepIndex(index + 1);
        }
      } else if (action === ACTIONS.PREV) {
        setStepIndex(Math.max(0, index - 1));
      }
    } else if (type === EVENTS.TARGET_NOT_FOUND) {
      // Should be rare now that we pre-filter, but if it happens, advance
      // (or finish if this was the last step).
      if (index + 1 >= filteredSteps.length) {
        markBackend(runKey, true);
        finishTour();
      } else {
        setStepIndex(index + 1);
      }
    }
  };

  const handleExitBadgeClick = () => {
    markBackend(runKey, false);
    cancelTour();
  };

  return (
    <>
      <Joyride
        steps={filteredSteps}
        stepIndex={stepIndex}
        run
        continuous
        scrollToFirstStep
        showSkipButton
        showProgress
        disableScrolling={false}
        hideCloseButton={false}
        disableOverlayClose={false}
        locale={{ back: 'Back', close: 'Close', last: 'Got it ✓', next: 'Next →', skip: 'Skip tour' }}
        styles={JOYRIDE_STYLES}
        callback={handleCallback}
      />
      {/* Always-visible escape hatch — guarantees the user can exit even if
          the inner Skip/Close buttons are misaligned by a Tailwind override. */}
      <button
        type="button"
        onClick={handleExitBadgeClick}
        data-testid="tour-exit-badge"
        aria-label="Exit tour"
        style={{
          position: 'fixed',
          top: 14,
          right: 14,
          zIndex: 10001,
          background: 'rgba(15,23,42,0.92)',
          color: '#f1f5f9',
          border: '1px solid rgba(212,175,55,0.5)',
          padding: '8px 12px',
          fontSize: 11,
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          fontWeight: 600,
          borderRadius: 6,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          cursor: 'pointer',
          boxShadow: '0 6px 24px rgba(0,0,0,0.4)',
        }}
      >
        <X size={12} /> Exit tour
      </button>
    </>
  );
}
